import { memo, useState, useCallback, useEffect, type CSSProperties } from 'react'
import { programCategoriesApi } from '../../lib/api.js'
import { COLLECTION_PRESET_COLORS } from '../../lib/catalog-colors.js'
import type { ProgramCategory } from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramCategoryModalProps {
  channelId:  string
  category?:  ProgramCategory
  onCreated?: (category: ProgramCategory) => void
  onUpdated?: (category: ProgramCategory) => void
  onClose:    () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const ProgramCategoryModal = memo(function ProgramCategoryModal({ channelId, category, onCreated, onUpdated, onClose }: ProgramCategoryModalProps) {
  const isEdit = !!category
  const [visible, setVisible] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  const [label,   setLabel]   = useState(category?.label ?? '')
  const [color,   setColor]   = useState(category?.color ?? COLLECTION_PRESET_COLORS[0].value)

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!label.trim()) return
    setSaving(true); setError(null)
    try {
      if (isEdit && category) {
        const updated = await programCategoriesApi.update(category.id, { label: label.trim(), color })
        onUpdated?.(updated)
      } else {
        const created = await programCategoriesApi.create({ channelId, label: label.trim(), color })
        onCreated?.(created)
      }
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [channelId, isEdit, category, label, color, onCreated, onUpdated])

  return (
    <ModalShell
      title={isEdit ? 'Modifier le format' : 'Nouveau format'}
      size="narrow"
      visible={visible}
      onClose={handleClose}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="program-category-label">Nom *</label>
            <input id="program-category-label" className="tp-input" value={label} onChange={e => setLabel(e.target.value)} required autoFocus placeholder="Ex: Documentaire, Sport, Magazine…" />
          </div>

          <fieldset className="tp-form-field">
            <legend className="tp-field-label">Couleur</legend>
            <div className="catalog-color-options">
              {COLLECTION_PRESET_COLORS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setColor(option.value)}
                  className={`catalog-color-option${color === option.value ? ' is-selected' : ''}`}
                  style={{ '--catalog-accent': option.value } as CSSProperties}
                  aria-label={option.label}
                  aria-pressed={color === option.value}
                />
              ))}
            </div>
          </fieldset>

          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">
              Annuler
            </button>
            <button type="submit" disabled={saving || !label.trim()} className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined}>
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})
